import { List } from 'immutable';

import SelectiveTransfer, { SelectiveTransferTab } from 'records/SelectiveTransfer';
import {
  SELECTIVE_TRANSFER_RESET,
  SELECTIVE_TRANSFER_SET_PAGE,
  SELECTIVE_TRANSFER_GO_NEXT,
  SELECTIVE_TRANSFER_GO_BACK,
  SELECTIVE_TRANSFER_CHANGE_TAB,
  SELECTIVE_TRANSFER_ASYNC_START,
  SELECTIVE_TRANSFER_SET_TAB_DATA,
  SELECTIVE_TRANSFER_SET_SELECTED_VIEW,
  SELECTIVE_TRANSFER_ASYNC_ERROR,
  SELECTIVE_TRANSFER_SET_SELECTIONS,
  SELECTIVE_TRANSFER_SET_INCLUDED_ASSETS,
  SELECTIVE_TRANSFER_INCLUDED_ASSET_SUCCESS,
  SELECTIVE_TRANSFER_REMOVE_DEPENDENCIES,
  SELECTIVE_TRANSFER_CHANGE_TRANSFER_NOTE,
  SELECTIVE_TRANSFER_SET_SOURCE,
  SELECTIVE_TRANSFER_SET_ASSET_AVAILABILITY,
  SELECTIVE_TRANSFER_ASYNC_FINISH,
  SELECTIVE_TRANSFER_SET_IMPORT_ACCOUNTS_OVERLAY,
  SELECTIVE_TRANSFER_SET_DESTINATION_ACCOUNTS,
  UPDATE_DESTINATION_ACCOUNTS_ON_CHECK,
  SELECTIVE_TRANSFER_SET_AT_THRESHOLD,
  UPDATE_DESTINATION_ACCOUNTS_ON_ALL_CHECK,
  SELECTIVE_TRANSFER_START_ASSET_TRANSFER,
} from 'actions/selectiveTransfer';
import { SELECTIVE_TRANSFER_PAGE, DEPENDENT_TABS } from 'constants/assetTransfer';
import { updateIncludedAssets, removeDependencies } from 'components/SelectiveTransfer/selectiveTransferHelper';

const assetTransferThreshold = Number(APP_CONFIG.assetTransferThreshold);

const initialState = new SelectiveTransfer();

const actionsMap = {
  [SELECTIVE_TRANSFER_RESET]: () => initialState,

  [SELECTIVE_TRANSFER_SET_PAGE]: (state, action) => state.set('currentPage', action.payload.page),

  [SELECTIVE_TRANSFER_GO_NEXT]: state => state.merge({
    currentPage: state.get('currentPage') < SELECTIVE_TRANSFER_PAGE.TRANSFER_SUMMARY ?
      state.get('currentPage') + 1 :
      SELECTIVE_TRANSFER_PAGE.TRANSFER_SUMMARY,
  }),

  [SELECTIVE_TRANSFER_GO_BACK]: state => state.merge({
    currentPage: state.get('currentPage') > SELECTIVE_TRANSFER_PAGE.SELECT_ASSETS ?
      state.get('currentPage') - 1 :
      SELECTIVE_TRANSFER_PAGE.SELECT_ASSETS,
  }),

  [SELECTIVE_TRANSFER_CHANGE_TAB]: (state, action) => state.set('currentTab', action.payload.tab),

  [SELECTIVE_TRANSFER_ASYNC_START]: state => state.set('isLoading', true),

  [SELECTIVE_TRANSFER_SET_TAB_DATA]: (state, action) => {
    const { tab, data } = action.payload;
    const tabData = state.get(tab).toJS();
    const { isObsolete, dependenciesLocked } = tabData;

    if (isObsolete && dependenciesLocked) {
      const updatedTab = updateIncludedAssets(tab, {
        ...tabData,
        data,
        isObsolete: false,
      }, tabData.links);
      return state.merge({
        isLoading: false,
        [tab]: new SelectiveTransferTab(updatedTab)
      });
    }

    return state.merge({
      isLoading: false,
      [tab]: new SelectiveTransferTab({
        isObsolete: false,
        isAllSelected: false,
        data: new List(data),
      })
    });
  },

  [SELECTIVE_TRANSFER_SET_SELECTED_VIEW]: (state, action) => state.set('isSelectedView', action.payload.makeVisible),

  [SELECTIVE_TRANSFER_ASYNC_ERROR]: (state, action) => {
    return state.merge({
      isLoading: false,
      errorMessage: action.error,
      isRequestingTransfer: false,
    });
  },

  [SELECTIVE_TRANSFER_SET_SELECTIONS]: (state, action) => {
    const { tab, data, selectedAssets, isAllSelected } = action.payload;

    const tabData = state.get(tab)
      .set('data', new List(data))
      .set('selectedAssets', selectedAssets)
      .set('isAllSelected', isAllSelected)
      .set('dependenciesLocked', false);

    return state.set(tab, tabData);
  },

  [SELECTIVE_TRANSFER_INCLUDED_ASSET_SUCCESS]: (state, action) => {
    const tabKey = action.payload.tabKey;
    return state
      .setIn([tabKey, 'dependenciesLocked'], true)
      .merge({ isLoading: false });
  },

  [SELECTIVE_TRANSFER_SET_INCLUDED_ASSETS]: (state, action) => {
    const { tabKey, links } = action.payload;
    let tabData = state.get(tabKey);
    const updatedTab = updateIncludedAssets(tabKey, tabData.toJS(), links);

    return state.set(tabKey, new SelectiveTransferTab(updatedTab));
  },

  [SELECTIVE_TRANSFER_REMOVE_DEPENDENCIES]: (state, action) => {
    let { source, tabKey } = action.payload;
    const queue = [{ parentIds: source, parentTab: tabKey }];
    let newState = state;

    for (let { parentIds, parentTab } of queue) {

      DEPENDENT_TABS[parentTab].forEach(tab => {

        const result = removeDependencies(parentIds, newState.get(tab).toJS());
        const removedSources = result.removed;
        newState = newState.set(tab, new SelectiveTransferTab(result.tabData));

        if (removedSources.length) {
          queue.push({ parentIds: removedSources, parentTab: tab });
        }
      });
    }

    return newState;
  },

  [SELECTIVE_TRANSFER_CHANGE_TRANSFER_NOTE]: (state, action) => state.merge({
    transferNote: action.payload.value,
  }),

  [SELECTIVE_TRANSFER_SET_SOURCE]: (state, action) => state.merge({
    source: action.payload.account,
  }),

  [SELECTIVE_TRANSFER_SET_ASSET_AVAILABILITY]: (state, action) => state.merge({
    sourceHasAssets: action.payload.hasAssets,
  }),

  [SELECTIVE_TRANSFER_ASYNC_FINISH]: state => state.merge({
    isLoading: false,
    isRequestingTransfer: false,
  }),

  [SELECTIVE_TRANSFER_SET_IMPORT_ACCOUNTS_OVERLAY]: (state, action) => state.set(
    'isImportView', action.payload.isOpen
  ),

  [SELECTIVE_TRANSFER_SET_DESTINATION_ACCOUNTS]: (state, action) => {
    // check if the imported accounts exists in destinations
    const newDestinations = action.payload.accounts.filter((account) => {
      return !state.destinations.some((destination) =>
       destination.get("accountId") === account.accountId);
    });
  
    const updatedDestinations = [...state.destinations, ...newDestinations];
  
    return state.merge({
      destinations: updatedDestinations,
      isLoading: false,
    });
  },

  [UPDATE_DESTINATION_ACCOUNTS_ON_CHECK]: (state, action) => {
    const destinations = state.get("destinations");
    const account = action.payload.account;
    const accountId = account.accountId;
    const isSelected = account.isSelected;

    let updatedDestinations = destinations.filter((account) => account.get("accountId") !== accountId);

    if (isSelected) {
      updatedDestinations = [...updatedDestinations, account];
    }

    return state.merge({
      destinations: updatedDestinations,
      isLoading: false,
    })
  },

  [UPDATE_DESTINATION_ACCOUNTS_ON_ALL_CHECK]: (state, action) => {
    const destinations = state.get('destinations');
    const accounts = action.payload.accounts;
    const isAllSelected = action.payload.isAllSelected;

    let updatedDestinations = destinations.filter((destination) =>
    !accounts.find(account => account.accountId === destination.get('accountId')));

    if (isAllSelected) {
      updatedDestinations = [...updatedDestinations, ...accounts];
    }

    return state.merge({
      destinations: updatedDestinations,
    })
  },

  [SELECTIVE_TRANSFER_SET_AT_THRESHOLD]: (state) => {

    const threshold = assetTransferThreshold - state.get('destinations').size;

    return state.set('assetTransferThreshold', threshold)
  },
  
  [SELECTIVE_TRANSFER_START_ASSET_TRANSFER]: state => state.set('isRequestingTransfer', true),

};

export default function selectiveTransfer(state = initialState, action = {}) {
  const fn = actionsMap[action.type];
  return fn ? fn(state, action) : state;
}
